import { useState } from 'react';
import api from '../services/api.js';

/**
 * usePreview — manages admin "Preview Portal" session state.
 *
 * State lives in sessionStorage so the preview dies when the tab closes.
 * The MSAL token is never replaced — the api.js interceptor overrides the
 * Authorization header with the preview token while a session is active.
 * The portal auth middleware (portalAuth.js) recognises preview tokens so
 * /api/customer-portal/* and /api/vendor-portal/* routes are correctly scoped.
 */
export function usePreview() {
  const [preview, setPreview] = useState(() => {
    try {
      const stored = sessionStorage.getItem('portalPreview');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const startPreview = async (entityType, entityId, role) => {
    const res = await api.post('/api/users/preview/start', { entityType, entityId, role });
    const data = {
      token:     res.data.token,
      context:   res.data.previewContext,
      startedAt: Date.now(),
    };
    sessionStorage.setItem('portalPreview', JSON.stringify(data));
    setPreview(data);
    // Full reload resets module-level caches so useCurrentUser re-fetches
    // with the preview role, restricting nav automatically.
    window.location.href = '/dashboard';
  };

  const endPreview = async (endReason = 'manual') => {
    try {
      await api.post('/api/users/preview/end', { endReason });
    } catch {
      // Always clear local session even if the API call fails (e.g. expired token)
    }
    sessionStorage.removeItem('portalPreview');
    setPreview(null);
    window.location.href = '/dashboard';
  };

  return { preview, startPreview, endPreview };
}
